import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getRoi, updateRoi, listRoiModels, getRoiModel, updateRoiModel } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Check, ChevronLeft, Calculator, Pencil, Plus, Clock } from "lucide-react";

// ---------------------------------------------------------------------------
// ROI Models admin (Phase 2A).
//
// EWM Implementation ROI stays on the legacy `roi_assumptions` doc (getRoi /
// updateRoi) — UNCHANGED. Cloud Migration strategy models are persisted in the
// new `roi_models` collection and edited via the roi-models API. No consultant
// wiring / ROI calculations here.
// ---------------------------------------------------------------------------

const STRUCTURE_LABELS = ["General", "Benefits", "Cost Drivers", "Investment", "Risk", "Advanced"];
const APPROACH_LABELS = { lift_shift: "Lift & Shift", lift_shift_validate: "Lift, Shift & Validate" };
const CATEGORY_LABELS = { migration: "Migration", implementation: "Implementation", optimization: "Optimization" };

const DEFAULT_FILTERS = { application: "all", evaluation_type: "all", category: "all", status: "all", search: "" };

const EVAL_SUBSECTIONS = {
  SmartEvaluate: [
    { key: "greenfield_brownfield", label: "Greenfield / Brownfield" },
    { key: "cloud_migration", label: "Cloud Migration Strategies", helper: "Each ROI model defines the financial assumptions used for a specific migration strategy." },
  ],
};
const APPLICATION_ORDER = ["SmartEvaluate", "SmartImprove", "SmartInspect"];

// Sprint 9K — SmartInspect obsolete time-based improvement assumptions. These
// are hidden from the Admin "Expected Improvements" tab (replaced by the global
// Process Benchmarks). Paper / Claims / Audit improvements are kept.
const SI_OBSOLETE_BENEFIT_KEYS = [
  "inspection_time_reduction",
  "administration_time_reduction",
  "photo_handling_improvement",
  "manual_sap_entry_reduction",
];

const STATUS_STYLES = {
  active: "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15",
  draft: "bg-[#FFF7ED] text-[#9A3412] border-[#FDBA74]/40",
  archived: "bg-[#F1F5F9] text-[#6B7280] border-[#E5E7EB]",
};

const humanize = (k) =>
  String(k).replace(/_/g, " ")
    .replace(/\bpct\b/gi, "%")
    .replace(/\bdr\b/gi, "DR")
    .replace(/\bsap\b/gi, "SAP")
    .replace(/\b\w/g, (c) => c.toUpperCase());

function getPath(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function setPath(prev, path, val) {
  const d = { ...(prev || {}) };
  const keys = path.split(".");
  let obj = d;
  for (let i = 0; i < keys.length - 1; i++) { obj[keys[i]] = { ...(obj[keys[i]] || {}) }; obj = obj[keys[i]]; }
  obj[keys[keys.length - 1]] = val;
  return d;
}
function countAssumptions(...objs) {
  let n = 0;
  const walk = (v) => { if (v == null) return; if (typeof v === "number") { n += 1; return; } if (typeof v === "object") Object.values(v).forEach(walk); };
  objs.forEach(walk);
  return n;
}

function NumField({ label, value, onChange, step = "1", hint }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#374151] mb-1 block">{label}</label>
      <Input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))} className="rounded-sm text-xs" />
      {hint && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{hint}</p>}
    </div>
  );
}
function TextField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#374151] mb-1 block">{label}</label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm text-xs" />
    </div>
  );
}
function FieldGrid({ obj, base, u, testid, meta = {}, omit = [] }) {
  const entries = Object.entries(obj || {}).filter(([k]) => !omit.includes(k));
  if (!entries.length) return <p className="text-xs text-[#9CA3AF] italic" data-testid={`${testid}-empty`}>No fields configured yet.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid={testid}>
      {entries.map(([k, v]) => {
        const path = `${base}.${k}`;
        const fm = meta[k] || {};
        const label = fm.label || humanize(k);
        if (typeof v === "string" || k === "indicative_duration" || k === "currency")
          return <TextField key={k} label={label} value={v} onChange={(x) => u(path, x)} />;
        return <NumField key={k} label={label} value={v} onChange={(x) => u(path, x)} step="0.01" hint={fm.help} />;
      })}
    </div>
  );
}
function Card({ title, subtitle, badge, children }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm mb-5">
      <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>{title}</h3>
          {subtitle && <p className="text-[11px] text-[#6B7280] mt-0.5">{subtitle}</p>}
        </div>
        {badge}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}
function ScopeBadge({ scope }) {
  const shared = scope === "shared";
  return (
    <span className={`shrink-0 inline-flex items-center px-2 py-0.5 border rounded-full text-[10px] font-medium ${shared ? "bg-[#EFF6FF] text-[#1D4ED8] border-[#1D4ED8]/20" : "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30"}`}>
      {shared ? "Shared across Technical Migration" : "Approach-specific"}
    </span>
  );
}

export default function RoiPage() {
  const navigate = useNavigate();
  const [legacy, setLegacy] = useState(null);
  const [cmModels, setCmModels] = useState([]);
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null); // { kind, key }
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const loadModels = useCallback(() => {
    listRoiModels().then((r) => setCmModels(r.models || [])).catch(() => {});
  }, []);
  useEffect(() => { getRoi().then(setLegacy).catch(() => {}); loadModels(); }, [loadModels]);

  const descriptors = useMemo(() => {
    const ewm = {
      id: "ewm_implementation", kind: "legacy", name: "EWM Implementation ROI",
      application: "SmartEvaluate", evaluation_type: "greenfield_brownfield",
      category: "Implementation", status: "active",
      structure: ["Workforce Information", "Benefit Drivers & Benchmarks", "EWM Cost Drivers", "Investment", "Risk"],
    };
    const cm = cmModels
      .filter((m) => (m.application || "smartevaluate") === "smartevaluate")
      .map((m) => ({
        id: m.model_key, kind: "cm", model: m, name: m.model_name,
        application: "SmartEvaluate", evaluation_type: "cloud_migration",
        category: CATEGORY_LABELS[m.category] || m.category, status: m.status,
        approaches: (m.supported_approaches || []).map((a) => APPROACH_LABELS[a] || a),
      }));
    const inspect = cmModels
      .filter((m) => m.application === "smartinspect")
      .map((m) => ({
        id: m.model_key, kind: "cm", model: m, name: m.model_name,
        application: "SmartInspect", evaluation_type: null,
        category: CATEGORY_LABELS[m.category] || humanize(m.category), status: m.status,
        structure: ["General", "Expected Improvements"],
      }));
    const si = {
      id: "si_optimization", kind: "external", name: "Warehouse Optimization ROI",
      application: "SmartImprove", evaluation_type: null, category: "Optimization",
      status: "active", route: "/admin/smartimprove/value-benchmarks",
    };
    return [ewm, ...cm, si, ...inspect];
  }, [cmModels]);

  const metricsFor = (d) => {
    if (d.kind === "legacy" && legacy) {
      return { assumptions: countAssumptions(legacy.efficiency, legacy.inventory, legacy.cost_drivers, legacy.workforce_volume, legacy.investment, legacy.realization, legacy.labor_improvement, legacy.error_reduction, legacy.throughput_improvement), updatedAt: legacy.updated_at || null };
    }
    if (d.kind === "cm") {
      const m = d.model;
      // Sprint 8R — SmartInspect count reflects ONLY the currently editable
      // assumptions (General financial + scenario multipliers + Expected
      // Improvement percentages). Removed/legacy benchmark groups are ignored.
      if (m.application === "smartinspect") {
        const ga = m.general_assumptions || {};
        const sm = ga.scenario_multipliers || {};
        const financial = ["analysis_period_years", "discount_rate_pct", "inflation_pct"]
          .filter((k) => typeof ga[k] === "number").length;
        const scenario = ["conservative_pct", "expected_pct", "optimistic_pct"]
          .filter((k) => typeof sm[k] === "number").length;
        const benefits = (m.benefit_categories || [])
          .filter((b) => !SI_OBSOLETE_BENEFIT_KEYS.includes(b.key))
          .reduce(
          (n, b) => n + ["low", "medium", "high"].filter((k) => typeof b[k] === "number").length, 0);
        return { assumptions: financial + scenario + benefits, updatedAt: m.updated_at || null };
      }
      return { assumptions: countAssumptions(m.general_assumptions, m.cost_drivers, m.investment_assumptions, m.risk_assumptions, m.approach_assumptions), updatedAt: m.updated_at || null };
    }
    return { assumptions: null, updatedAt: null };
  };

  const anyFilterActive = Object.keys(DEFAULT_FILTERS).some((k) => filters[k] !== DEFAULT_FILTERS[k]);
  const filtered = useMemo(() => {
    const q = (filters.search || "").trim().toLowerCase();
    return descriptors.filter((m) => {
      if (filters.application !== "all" && m.application !== filters.application) return false;
      if (filters.evaluation_type !== "all" && (m.evaluation_type || "") !== filters.evaluation_type) return false;
      if (filters.category !== "all" && m.category !== filters.category) return false;
      if (filters.status !== "all" && m.status !== filters.status) return false;
      if (q && !`${m.name} ${m.category} ${m.application}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [filters, descriptors]);

  const openModel = (d) => {
    if (d.kind === "external") { navigate(d.route); return; }
    setEditing({ kind: d.kind, key: d.id });
    setMode("edit");
  };
  const back = () => { setMode("list"); setEditing(null); loadModels(); };

  if (mode === "edit" && editing?.kind === "legacy") {
    return <EwmEditor data={legacy} setData={setLegacy} onBack={back} />;
  }
  if (mode === "edit" && editing?.kind === "cm") {
    return <CmRoiModelEditor modelKey={editing.key} onBack={back} />;
  }

  return (
    <div data-testid="roi-models-page">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>ROI Models</h1>
        <p className="text-[11px] text-[#6B7280] mt-1 max-w-3xl leading-relaxed">
          Configure reusable ROI models used by SmartEvaluate and future WarehouseX products. ROI Models
          define default financial assumptions, benefit percentages, investment drivers, and risk factors
          that are automatically applied to new assessments.
        </p>
      </div>

      <RoiFilterBar filters={filters} setFilters={setFilters} />

      {!legacy ? (
        <div className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-center text-xs text-[#9CA3AF]">Loading ROI models…</div>
      ) : anyFilterActive ? (
        <FilteredGrid models={filtered} metricsFor={metricsFor} onOpen={openModel} />
      ) : (
        <GroupedList models={descriptors} metricsFor={metricsFor} onOpen={openModel} />
      )}
    </div>
  );
}

function RoiFilterBar({ filters, setFilters }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const evalOpts = (filters.application === "SmartImprove" || filters.application === "SmartInspect")
    ? [["all", "Not Applicable"]]
    : [["all", "All Evaluation Types"], ["greenfield_brownfield", "Greenfield / Brownfield"], ["cloud_migration", "Cloud Migration"]];
  const cls = "px-2.5 py-1.5 text-xs border border-[#E5E7EB] rounded-sm bg-white text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979]";
  return (
    <div data-testid="roi-filter-bar" className="mb-5 flex flex-wrap items-center gap-2">
      <select data-testid="roi-filter-application" className={cls} value={filters.application} onChange={(e) => { set("application", e.target.value); set("evaluation_type", "all"); }}>
        <option value="all">All Applications</option>
        <option value="SmartEvaluate">SmartEvaluate</option>
        <option value="SmartImprove">SmartImprove</option>
        <option value="SmartInspect">SmartInspect</option>
      </select>
      <select data-testid="roi-filter-evaluation-type" className={cls} value={filters.evaluation_type} onChange={(e) => set("evaluation_type", e.target.value)}>
        {evalOpts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select data-testid="roi-filter-category" className={cls} value={filters.category} onChange={(e) => set("category", e.target.value)}>
        <option value="all">All Categories</option>
        <option value="Implementation">Implementation</option>
        <option value="Migration">Migration</option>
        <option value="Optimization">Optimization</option>
        <option value="Inspection Digitization">Inspection Digitization</option>
      </select>
      <select data-testid="roi-filter-status" className={cls} value={filters.status} onChange={(e) => set("status", e.target.value)}>
        <option value="all">All Status</option>
        <option value="active">Active</option>
        <option value="draft">Draft</option>
        <option value="archived">Archived</option>
      </select>
      <Input data-testid="roi-filter-search" value={filters.search} onChange={(e) => set("search", e.target.value)} placeholder="Search ROI models…" className="rounded-sm text-xs h-[34px] w-56" />
      {Object.keys(DEFAULT_FILTERS).some((k) => filters[k] !== DEFAULT_FILTERS[k]) && (
        <button data-testid="roi-filter-clear" onClick={() => setFilters(DEFAULT_FILTERS)} className="text-[11px] text-[#025979] underline">Clear</button>
      )}
    </div>
  );
}

function FilteredGrid({ models, metricsFor, onOpen }) {
  if (models.length === 0) return <div data-testid="roi-filtered-empty" className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-center text-xs text-[#9CA3AF]">No ROI models match the current filters.</div>;
  return (
    <div data-testid="roi-filtered-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {models.map((m) => <RoiModelCard key={m.id} m={m} metrics={metricsFor(m)} onOpen={onOpen} />)}
    </div>
  );
}

function GroupedList({ models, metricsFor, onOpen }) {
  const apps = APPLICATION_ORDER.filter((a) => models.some((m) => m.application === a));
  return (
    <div className="space-y-8">
      {apps.map((app) => {
        const appModels = models.filter((m) => m.application === app);
        const sections = EVAL_SUBSECTIONS[app]
          ? EVAL_SUBSECTIONS[app].map((s) => ({ label: s.label, helper: s.helper, key: s.key, rows: appModels.filter((m) => m.evaluation_type === s.key) })).filter((s) => s.rows.length > 0)
          : [{ label: null, helper: null, key: "_", rows: appModels }];
        return (
          <section key={app} data-testid={`roi-app-${app}`}>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-[#025979]/20">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#025979]" style={{ fontFamily: "Chivo" }}>{app}</h2>
            </div>
            <div className="space-y-6">
              {sections.map((s) => (
                <div key={s.key} data-testid={`roi-section-${s.key}`}>
                  {s.label && <p className="text-[12px] font-semibold text-[#0F172A] mb-1">{s.label}</p>}
                  {s.helper && <p className="text-[11px] text-[#6B7280] mb-3">{s.helper}</p>}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {s.rows.map((m) => <RoiModelCard key={m.id} m={m} metrics={metricsFor(m)} onOpen={onOpen} />)}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function RoiModelCard({ m, metrics, onOpen }) {
  const isExternal = m.kind === "external";
  const isDraft = m.status === "draft";
  return (
    <article data-testid={`roi-card-${m.id}`} className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{m.category}</p>
          <h3 className="text-base font-semibold text-[#111827] leading-tight">{m.name}</h3>
        </div>
        <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 border rounded-sm text-[10px] font-medium capitalize ${STATUS_STYLES[m.status] || STATUS_STYLES.archived}`}>{m.status}</span>
      </div>

      {m.approaches?.length > 0 && (
        <div className="mb-3" data-testid={`roi-card-approaches-${m.id}`}>
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Implementation Approaches</p>
          <ul className="space-y-0.5">
            {m.approaches.map((a) => (
              <li key={a} className="text-xs text-[#111827] flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#025979]" /> {a}</li>
            ))}
          </ul>
        </div>
      )}

      {isExternal ? (
        <p className="text-[11px] text-[#6B7280] mb-4">Managed in the SmartImprove ROI Models workspace.</p>
      ) : (
        <div className="mb-4">
          <p className="text-xs text-[#111827] font-semibold mb-2 flex items-center gap-1">
            <Calculator className="w-3 h-3 text-[#025979]" />
            {metrics.assumptions == null ? "—" : metrics.assumptions} Assumptions
          </p>
          <div className="flex flex-wrap gap-1" data-testid={`roi-structure-${m.id}`}>
            {(m.structure || STRUCTURE_LABELS).map((s) => <span key={s} className="text-[10px] text-[#475569] bg-[#F1F5F9] border border-[#E5E7EB] rounded-full px-2 py-0.5">{s}</span>)}
          </div>
        </div>
      )}

      <p className="text-[10px] text-[#9CA3AF] mb-3 flex items-center gap-1">
        <Clock className="w-3 h-3" /> Last updated: {metrics.updatedAt ? new Date(metrics.updatedAt).toLocaleDateString() : "—"}
      </p>

      <div className="mt-auto flex justify-end">
        <Button data-testid={`roi-open-${m.id}`} onClick={() => onOpen(m)} variant={isDraft ? "outline" : "default"}
          className={isDraft ? "rounded-sm text-xs" : "bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"}>
          {isDraft ? <><Plus className="w-3 h-3 mr-1" /> Create Model</> : <><Pencil className="w-3 h-3 mr-1" /> {isExternal ? "Open Model" : "Edit Model"}</>}
        </Button>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Cloud Migration ROI model editor — approach selector + shared/approach tabs.
// ---------------------------------------------------------------------------
const CM_TABS = [["general", "General"], ["benefits", "Benefits"], ["cost_drivers", "Cost Drivers"], ["investment", "Investment"], ["risk", "Risk"], ["advanced", "Advanced"]];

// Sprint 7A — consistent labels + helper text for SmartInspect ROI Model fields.
const SI_FIELD_META = {
  default_labour_cost_per_hour: { label: "Default Labour Cost", help: "Default hourly labour cost used when the customer does not provide actual values." },
  device_life_years: { label: "SmartInspect Device Useful Life", help: "Expected SmartInspect device life (years) before replacement." },
  device_useful_life: { label: "Current Device Useful Life", help: "Expected useful life (years) of the current inspection device before replacement." },
  cloud_storage_cost_per_gb_per_month: { label: "Cloud Storage Cost Per GB Per Month", help: "Average cloud storage cost used when the customer does not provide actual values." },
  annual_archive_storage_cost: { label: "Annual Archive Storage Cost", help: "Benchmark annual archive storage cost." },
  paper_cost_per_inspection: { label: "Paper Cost Per Inspection", help: "Benchmark paper & printing cost per inspection." },
  mobile_device_cost: { label: "Mobile Device Cost", help: "Benchmark cost of one SmartInspect mobile device." },
  digital_camera_cost: { label: "Digital Camera Cost", help: "Benchmark cost of a digital inspection camera." },
  printer_cost: { label: "Printer Cost", help: "Benchmark printer cost." },
  annual_it_support_cost: { label: "Annual IT Support Cost", help: "Benchmark annual IT support cost." },
  annual_device_maintenance_pct: { label: "Annual Device Maintenance %", help: "Annual device maintenance as a percentage of device cost." },
  expected_smartinspect_inspection_time: { label: "Expected SmartInspect Inspection Time", help: "Default inspection duration (minutes) when the customer future process is unknown." },
  expected_smartinspect_admin_time: { label: "Expected SmartInspect Administration Time", help: "Default administration duration (minutes) when the customer future process is unknown." },
  manual_data_entry_time: { label: "Default Manual Entry Time", help: "Default manual data entry time (minutes) when not provided." },
  number_of_smartinspect_devices: { label: "Number of SmartInspect Devices", help: "Default SmartInspect device count when not provided by the customer." },
  cloud_storage_growth_pct: { label: "Cloud Storage Growth %", help: "Expected annual cloud storage growth." },
  photo_compression_pct: { label: "Photo Compression %", help: "Expected photo storage reduction from compression." },
  mobile_device_replacement_pct: { label: "Mobile Device Replacement %", help: "Expected annual device replacement rate." },
  annual_salary_growth_pct: { label: "Annual Salary Growth %", help: "Expected annual salary inflation applied over the analysis period." },
  discount_rate_pct: { label: "Discount Rate %", help: "Discount rate used for NPV." },
  inflation_pct: { label: "Inflation %", help: "General inflation applied to recurring costs and benefits." },
  analysis_period_years: { label: "Analysis Period (Years)", help: "Number of years modelled for multi-year ROI." },
};

function CmRoiModelEditor({ modelKey, onBack }) {
  const [m, setM] = useState(null);
  const [tab, setTab] = useState("general");
  const [approach, setApproach] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getRoiModel(modelKey).then((doc) => {
      setM(doc);
      setApproach((doc.supported_approaches || [])[0] || null);
    }).catch(() => toast.error("Failed to load ROI model"));
  }, [modelKey]);

  const u = (path, val) => setM((prev) => setPath(prev, path, val));
  const updateBenefit = (i, field, val) => setM((prev) => {
    const arr = [...(prev.benefit_categories || [])];
    arr[i] = { ...arr[i], [field]: val };
    return { ...prev, benefit_categories: arr };
  });

  const save = async () => {
    setSaving(true);
    try {
      const saved = await updateRoiModel(modelKey, m);
      setM(saved);
      toast.success("ROI model saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save ROI model");
    } finally { setSaving(false); }
  };

  if (!m) return <p className="text-sm text-[#6B7280]">Loading…</p>;
  const approaches = m.supported_approaches || [];
  const benefits = m.benefit_categories || [];
  const isSI = m.application === "smartinspect";
  // Sprint 9K — hide obsolete SmartInspect improvements from the UI while keeping
  // their stored values (ROI math unchanged until the engine adopts benchmarks).
  const visibleBenefits = (isSI
    ? benefits.map((b, idx) => ({ b, idx })).filter(({ b }) => !SI_OBSOLETE_BENEFIT_KEYS.includes(b.key))
    : benefits.map((b, idx) => ({ b, idx })));
  const fmeta = isSI ? SI_FIELD_META : {};
  const baseTabs = CM_TABS.map(([k, l]) => [k, k === "benefits" && isSI ? "Expected Improvements" : l]);
  let tabs = baseTabs;
  if (isSI) {
    // Sprint 8Q — SmartInspect ROI Model has exactly two tabs.
    tabs = [["general", "General"], ["benefits", "Expected Improvements"]];
  }

  return (
    <div data-testid="roi-edit-page">
      <button onClick={onBack} className="text-[#025979] hover:text-[#03738C] text-xs inline-flex items-center gap-1 mb-4" data-testid="roi-back-btn">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to ROI Models
      </button>

      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{CATEGORY_LABELS[m.category] || humanize(m.category)} · {isSI ? "SmartInspect" : "Cloud Migration Strategies"}</p>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }} data-testid="roi-edit-title">{m.model_name}</h1>
          <span className={`mt-1 inline-flex items-center px-1.5 py-0.5 border rounded-sm text-[10px] font-medium capitalize ${STATUS_STYLES[m.status] || STATUS_STYLES.archived}`}>{m.status}</span>
        </div>
        <Button data-testid="save-roi-btn" onClick={save} disabled={saving} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs disabled:opacity-50">
          <Check className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      {/* Approach selector (only if the model supports multiple approaches) */}
      {approaches.length > 0 && (
        <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3 mb-5" data-testid="roi-approach-selector">
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2">Implementation Approach</p>
          <div className="flex flex-wrap gap-2">
            {approaches.map((a) => (
              <button key={a} data-testid={`roi-approach-${a}`} onClick={() => setApproach(a)}
                className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${approach === a ? "border-[#025979] bg-[#E0F2FE] text-[#025979] font-semibold" : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#025979]/40"}`}>
                {APPROACH_LABELS[a] || a}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#9CA3AF] mt-2">General, Benefits, Cost Drivers, Investment &amp; Risk are shared across Technical Migration. The Advanced tab holds approach-specific settings.</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-[#E5E7EB] mb-5" data-testid="roi-edit-tabs">
        {tabs.map(([key, label]) => (
          <button key={key} data-testid={`roi-tab-${key}`} onClick={() => setTab(key)}
            className={`px-3 py-2 text-xs font-medium rounded-t-sm transition-colors ${tab === key ? "text-[#025979] border-b-2 border-[#025979] -mb-px" : "text-[#6B7280] hover:text-[#111827]"}`}>{label}</button>
        ))}
      </div>

      {tab === "general" && isSI && (
        <>
          <Card title="Financial Assumptions" subtitle="Core financial inputs used for multi-year ROI and NPV.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="roi-si-financial-fields">
              <NumField label="Analysis Period (Years)" value={m.general_assumptions?.analysis_period_years}
                onChange={(x) => u("general_assumptions.analysis_period_years", x)} step="1"
                hint="Number of years modelled for multi-year ROI." />
              <NumField label="Discount Rate %" value={m.general_assumptions?.discount_rate_pct}
                onChange={(x) => u("general_assumptions.discount_rate_pct", x)} step="0.1"
                hint="Discount rate used for NPV." />
              <NumField label="Inflation %" value={m.general_assumptions?.inflation_pct}
                onChange={(x) => u("general_assumptions.inflation_pct", x)} step="0.1"
                hint="General inflation applied to recurring costs and benefits." />
            </div>
          </Card>
          <Card title="Scenario Multipliers" subtitle="Per-scenario realization applied to the ROI results. 100% means no adjustment (no hidden discount).">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="roi-si-scenario-fields">
              <NumField label="Conservative %" value={m.general_assumptions?.scenario_multipliers?.conservative_pct}
                onChange={(x) => u("general_assumptions.scenario_multipliers.conservative_pct", x)} step="1"
                hint="Applied when the Conservative scenario is selected." />
              <NumField label="Expected %" value={m.general_assumptions?.scenario_multipliers?.expected_pct}
                onChange={(x) => u("general_assumptions.scenario_multipliers.expected_pct", x)} step="1"
                hint="Applied when the Expected scenario is selected." />
              <NumField label="Optimistic %" value={m.general_assumptions?.scenario_multipliers?.optimistic_pct}
                onChange={(x) => u("general_assumptions.scenario_multipliers.optimistic_pct", x)} step="1"
                hint="Applied when the Optimistic scenario is selected." />
            </div>
          </Card>
          <Card title="SmartInspect Process Benchmarks" subtitle="Global SmartInspect future-state activity times (minutes per inspection). These are fixed global benchmarks — not regional, not currency, and not customer-editable.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="roi-si-process-benchmark-fields">
              <NumField label="Visual Inspection Time (min)" value={m.process_benchmarks?.visual_inspection_time}
                onChange={(x) => u("process_benchmarks.visual_inspection_time", x)} step="0.1"
                hint="SmartInspect future-state visual inspection time per inspection." />
              <NumField label="Photograph Capture Time (min)" value={m.process_benchmarks?.photograph_capture_time}
                onChange={(x) => u("process_benchmarks.photograph_capture_time", x)} step="0.1"
                hint="SmartInspect future-state photograph capture time per inspection." />
              <NumField label="Inspection Code Recording Time (min)" value={m.process_benchmarks?.code_recording_time}
                onChange={(x) => u("process_benchmarks.code_recording_time", x)} step="0.1"
                hint="SmartInspect future-state inspection code recording time per inspection." />
              <NumField label="Administration & Reporting Time (min)" value={m.process_benchmarks?.administration_reporting_time}
                onChange={(x) => u("process_benchmarks.administration_reporting_time", x)} step="0.1"
                hint="SmartInspect future-state administration & reporting time per inspection." />
            </div>
          </Card>
        </>
      )}

      {tab === "general" && !isSI && (
        <Card title="Global Assumptions" subtitle="Global financial assumptions applied across the model." badge={<ScopeBadge scope="shared" />}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <TextField label="Currency" value={m.currency} onChange={(x) => u("currency", x)} />
          </div>
          <FieldGrid obj={m.general_assumptions} base="general_assumptions" u={u} testid="roi-general-fields" meta={fmeta} />
        </Card>
      )}

      {tab === "benefits" && (
        <Card title={isSI ? "Expected Improvements" : "Benefit Categories"} subtitle="Conservative / Expected / Optimistic benefit percentages by improvement." badge={isSI ? null : <ScopeBadge scope="shared" />}>
          {visibleBenefits.length === 0 ? (
            <p className="text-xs text-[#9CA3AF] italic" data-testid="roi-benefits-empty">No benefit categories configured yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="roi-benefits-table">
                <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Benefit</th>
                    <th className="px-3 py-2 font-semibold w-24">{isSI ? "Conservative" : "Low"}</th>
                    <th className="px-3 py-2 font-semibold w-24">{isSI ? "Expected" : "Medium"}</th>
                    <th className="px-3 py-2 font-semibold w-24">{isSI ? "Optimistic" : "High"}</th>
                    <th className="px-3 py-2 font-semibold w-16">Unit</th>
                    <th className="px-3 py-2 font-semibold w-16">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleBenefits.map(({ b, idx }) => (
                    <tr key={b.key || idx} className="border-t border-[#F1F5F9] align-top" data-testid={`roi-benefit-row-${b.key || idx}`}>
                      <td className="px-3 py-2">
                        <p className="font-semibold text-[#111827]">{b.name}</p>
                        {b.description && <p className="text-[10px] text-[#9CA3AF] mt-0.5 max-w-md leading-relaxed">{b.description}</p>}
                      </td>
                      {["low", "medium", "high"].map((k) => (
                        <td key={k} className="px-3 py-2">
                          <Input type="number" step="0.1" value={b[k] ?? ""} onChange={(e) => updateBenefit(idx, k, e.target.value === "" ? null : parseFloat(e.target.value))} className="rounded-sm text-xs h-8" />
                        </td>
                      ))}
                      <td className="px-3 py-2 text-[#6B7280]">{b.unit || "%"}</td>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={b.active !== false} onChange={(e) => updateBenefit(idx, "active", e.target.checked)} data-testid={`roi-benefit-active-${b.key || idx}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "cost_drivers" && (
        <Card title="Cost Drivers" subtitle="Unit and annual cost benchmarks only — no percentages." badge={isSI ? null : <ScopeBadge scope="shared" />}>
          <FieldGrid obj={m.cost_drivers} base="cost_drivers" u={u} testid="roi-cost-fields" meta={fmeta} omit={isSI ? ["default_labour_cost_per_hour"] : []} />
        </Card>
      )}

      {tab === "investment" && (
        <Card title="Investment Assumptions" subtitle="Default investment structure. Consultant-entered Implementation costs remain the primary source at runtime." badge={isSI ? null : <ScopeBadge scope="shared" />}>
          <FieldGrid obj={m.investment_assumptions} base="investment_assumptions" u={u} testid="roi-investment-fields" meta={fmeta} />
        </Card>
      )}

      {tab === "risk" && (
        <Card title="Risk & Realization" subtitle="Confidence, realization ramp-up and sensitivity assumptions." badge={isSI ? null : <ScopeBadge scope="shared" />}>
          <FieldGrid obj={m.risk_assumptions} base="risk_assumptions" u={u} testid="roi-risk-fields" meta={fmeta} />
        </Card>
      )}

      {tab === "advanced" && (
        <Card
          title={approach ? `Approach-Specific Assumptions — ${APPROACH_LABELS[approach] || approach}` : "Advanced Assumptions"}
          subtitle={approach ? "Effort, testing and cutover multipliers, risk buffer and indicative duration for the selected implementation approach." : "Infrequently edited settings."}
          badge={isSI ? null : <ScopeBadge scope="approach" />}
        >
          {approach ? (
            <FieldGrid obj={m.approach_assumptions?.[approach]} base={`approach_assumptions.${approach}`} u={u} testid="roi-approach-fields" meta={fmeta} />
          ) : (
            <FieldGrid obj={m.advanced_assumptions} base="advanced_assumptions" u={u} testid="roi-advanced-fields" meta={fmeta} />
          )}
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EWM Implementation ROI editor — legacy roi_assumptions doc. UNCHANGED fields.
// ---------------------------------------------------------------------------
const EWM_TABS = [["workforce", "Workforce Information"], ["benefit_drivers", "Benefit Drivers & Benchmarks"], ["ewm_cost_drivers", "EWM Cost Drivers"], ["investment", "Investment"], ["risk", "Risk"]];
// Approved granular benefit-driver methodology — five sections; one STANDARD
// assumption per driver (Conservative/Expected/Optimistic handled in Risk).
const BENEFIT_DRIVER_SECTIONS = [
  { key: "s1", title: "S1 — Inbound & Warehouse Process Efficiency", drivers: [
    ["rf_directed_receiving", "RF-Directed Receiving & GR Posting"],
    ["system_directed_putaway", "System-Directed Putaway"],
    ["hu_barcode_scanning", "HU Barcode Scanning & Label Printing"],
    ["cycle_count_rf", "Cycle Count via RF"],
    ["warehouse_monitor_exception", "Warehouse Monitor & Exception Management"],
    ["asn_based_receiving", "ASN-Based Receiving"],
    ["deconsolidation_dock", "Deconsolidation at Receiving Dock"],
  ] },
  { key: "s2", title: "S2 — Outbound Efficiency & Shipping Accuracy", drivers: [
    ["wave_management_pickpath", "Wave Management & Pick Path Optimization"],
    ["task_interleaving", "Task Interleaving"],
    ["staging_destination_assignment", "Staging with Destination Assignment"],
    ["rf_loading_shipment_confirmation", "RF-Based Loading & Shipment Confirmation"],
    ["shipping_error_mispick_reduction", "Shipping Error / Mispick Reduction"],
    ["customer_penalty_sla_avoidance", "Customer Penalty / SLA Avoidance"],
  ] },
  { key: "s3", title: "S3 — Admin / Back-Office", drivers: [
    ["report_generation", "Report Generation"],
    ["returns_processing", "Returns Processing"],
    ["tpl_reconciliation_discrepancy", "3PL Reconciliation & Discrepancy"],
    ["quality_compliance_documentation", "Quality / Compliance Documentation"],
  ] },
  { key: "s4", title: "S4 — Inventory Accuracy & Shrinkage", drivers: [
    ["annual_shrinkage_base", "Annual Shrinkage / Write-offs Base"],
    ["s4_shrinkage_reduction", "Shrinkage Reduction"],
    ["physical_count_elimination", "Physical Count Elimination"],
    ["cycle_count_accuracy_improvement", "Cycle Count Accuracy Improvement"],
  ] },
  { key: "s5", title: "S5 — Cost Avoidance & Operational Efficiency", drivers: [
    ["production_stoppages_avoided", "Production Stoppages Avoided"],
    ["fill_rate_improvement", "Fill Rate Improvement"],
    ["tpl_reconciliation_cost_reduction", "3PL Reconciliation Cost Reduction"],
    ["compliance_audit_readiness", "Compliance & Audit Readiness"],
    ["reduced_expediting_procurement", "Reduced Expediting / Emergency Procurement"],
    ["offsite_location_management", "Offsite Location Management"],
    ["labour_savings_parts_location", "Labour Savings — Parts Location"],
  ] },
];
// Default Value Type classification (metadata only — not used by the ROI engine).
const DEFAULT_VALUE_TYPE = {
  overall_labour_productivity: "Standard Benchmark",
  // S1–S3 = Standard Benchmark
  rf_directed_receiving: "Standard Benchmark", system_directed_putaway: "Standard Benchmark",
  hu_barcode_scanning: "Standard Benchmark", cycle_count_rf: "Standard Benchmark",
  warehouse_monitor_exception: "Standard Benchmark", asn_based_receiving: "Standard Benchmark",
  deconsolidation_dock: "Standard Benchmark", wave_management_pickpath: "Standard Benchmark",
  task_interleaving: "Standard Benchmark", staging_destination_assignment: "Standard Benchmark",
  rf_loading_shipment_confirmation: "Standard Benchmark", shipping_error_mispick_reduction: "Standard Benchmark",
  customer_penalty_sla_avoidance: "Standard Benchmark", report_generation: "Standard Benchmark",
  returns_processing: "Standard Benchmark", tpl_reconciliation_discrepancy: "Standard Benchmark",
  quality_compliance_documentation: "Standard Benchmark",
  // S4 (per approved classification)
  s4_shrinkage_reduction: "Standard Benchmark", physical_count_elimination: "Reference Customer",
  cycle_count_accuracy_improvement: "Reference Customer",
  // S5 (per approved classification)
  production_stoppages_avoided: "Reference Customer", fill_rate_improvement: "Standard Benchmark",
  tpl_reconciliation_cost_reduction: "Reference Customer", compliance_audit_readiness: "Standard Benchmark",
  reduced_expediting_procurement: "Reference Customer", offsite_location_management: "Reference Customer",
  labour_savings_parts_location: "Reference Customer",
};

function EwmEditor({ data, setData, onBack }) {
  const [tab, setTab] = useState("workforce");
  const [msg, setMsg] = useState("");
  const u = (path, val) => setData((prev) => setPath(prev, path, val));
  const save = async () => {
    try {
      const result = await updateRoi(data);
      setData(result);
      setMsg("Saved");
      toast.success("ROI model saved");
      setTimeout(() => setMsg(""), 2000);
    } catch { setMsg("Error saving"); toast.error("Error saving ROI model"); setTimeout(() => setMsg(""), 3000); }
  };
  const cost = data.cost_drivers || {};
  const wf = data.workforce_volume || {};
  const inv = data.investment || {};
  const real = data.realization || {};

  return (
    <div data-testid="roi-edit-page">
      <button onClick={onBack} className="text-[#025979] hover:text-[#03738C] text-xs inline-flex items-center gap-1 mb-4" data-testid="roi-back-btn">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to ROI Models
      </button>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Implementation · Greenfield / Brownfield</p>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>EWM Implementation ROI</h1>
        </div>
        <Button data-testid="save-roi-btn" onClick={save} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
          <Check className="w-3 h-3 mr-1" /> Save Changes
        </Button>
      </div>
      {msg && <div data-testid="roi-msg" className="mb-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979] font-medium">{msg}</div>}
      <div className="flex flex-wrap gap-1 border-b border-[#E5E7EB] mb-5" data-testid="roi-edit-tabs">
        {EWM_TABS.map(([key, label]) => (
          <button key={key} data-testid={`roi-tab-${key}`} onClick={() => setTab(key)}
            className={`px-3 py-2 text-xs font-medium rounded-t-sm transition-colors ${tab === key ? "text-[#025979] border-b-2 border-[#025979] -mb-px" : "text-[#6B7280] hover:text-[#111827]"}`}>{label}</button>
        ))}
      </div>

      {tab === "workforce" && (
        <Card title="Workforce Information" subtitle="Reusable working-calendar defaults. Customer-specific labour cost, operating hours and operatives come from Warehouse Groups.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumField label="Hours per Week" value={data.workforce_information?.hours_per_week} onChange={(x) => u("workforce_information.hours_per_week", x)} />
            <NumField label="Days per Week" value={data.workforce_information?.days_per_week} onChange={(x) => u("workforce_information.days_per_week", x)} />
            <NumField label="Weeks per Year" value={data.workforce_information?.weeks_per_year} onChange={(x) => u("workforce_information.weeks_per_year", x)} />
          </div>
        </Card>
      )}
      {tab === "benefit_drivers" && (
        <div className="space-y-5" data-testid="roi-benefit-drivers">
          {BENEFIT_DRIVER_SECTIONS.map((section) => (
            <Card key={section.key} title={section.title} subtitle="One standard assumption per driver. Conservative / Expected / Optimistic treatment is handled in Risk.">
              <div>
                <table className="w-full text-xs table-fixed" data-testid={`roi-bd-table-${section.key}`}>
                  <colgroup>
                    <col style={{ width: "22%" }} /><col style={{ width: "13%" }} />
                    <col style={{ width: "29%" }} /><col style={{ width: "21%" }} /><col style={{ width: "15%" }} />
                  </colgroup>
                  <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Model Input</th>
                      <th className="px-3 py-2 font-semibold">Value Used</th>
                      <th className="px-3 py-2 font-semibold">Benchmark / Basis</th>
                      <th className="px-3 py-2 font-semibold">Source</th>
                      <th className="px-3 py-2 font-semibold">URL / Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.drivers.map(([key, name]) => {
                      const v = (data.benefit_drivers && data.benefit_drivers[key]) || {};
                      const base = `benefit_drivers.${key}`;
                      const ta = "w-full resize-none rounded-sm text-xs border border-[#E5E7EB] bg-white px-2 py-1 whitespace-pre-wrap break-words leading-snug focus:outline-none focus:ring-1 focus:ring-[#025979]";
                      const grow = (e) => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; };
                      return (
                        <tr key={key} className="border-t border-[#F1F5F9] align-top" data-testid={`roi-bd-row-${key}`}>
                          <td className="px-3 py-2 font-semibold text-[#111827] break-words whitespace-normal">{name}</td>
                          <td className="px-3 py-1.5">
                            <textarea rows={1} data-testid={`roi-bd-${key}-value_used`} value={v.value_used ?? ""}
                              ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                              onInput={grow} onChange={(e) => u(`${base}.value_used`, e.target.value)}
                              className={ta} placeholder="e.g. 3 min/day saved" />
                          </td>
                          <td className="px-3 py-1.5">
                            <textarea rows={1} data-testid={`roi-bd-${key}-external_reference`} value={v.external_reference ?? ""}
                              ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                              onInput={grow} onChange={(e) => u(`${base}.external_reference`, e.target.value)}
                              className={ta} placeholder="Rationale / basis for the value used" />
                          </td>
                          <td className="px-3 py-1.5">
                            <textarea rows={1} data-testid={`roi-bd-${key}-source`} value={v.source ?? ""}
                              ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                              onInput={grow} onChange={(e) => u(`${base}.source`, e.target.value)}
                              className={ta} placeholder="e.g. Aberdeen Group WMS Benchmark" />
                          </td>
                          <td className="px-3 py-1.5">
                            <textarea rows={1} data-testid={`roi-bd-${key}-reference_url`} value={v.reference_url ?? ""}
                              ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                              onInput={grow} onChange={(e) => u(`${base}.reference_url`, e.target.value)}
                              className={ta} placeholder="URL or citation" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}
      {tab === "ewm_cost_drivers" && (
        <Card title="EWM Cost Drivers" subtitle="Pre-filled Benchmarks — Admin defaults that customer actuals can later override.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumField label="Annual Inventory Shrinkage / Write-offs" value={cost.annual_inventory_shrinkage} onChange={(x) => u("cost_drivers.annual_inventory_shrinkage", x)} />
            <NumField label="Cost of 1 Hour Unplanned Downtime" value={cost.unplanned_downtime_cost_per_hour} onChange={(x) => u("cost_drivers.unplanned_downtime_cost_per_hour", x)} />
            <NumField label="Production Stoppages per Month" value={cost.production_stoppages_per_month} onChange={(x) => u("cost_drivers.production_stoppages_per_month", x)} />
            <NumField label="Avg. Stoppage Duration (hours)" value={cost.avg_stoppage_duration_hours} onChange={(x) => u("cost_drivers.avg_stoppage_duration_hours", x)} step="0.1" />
            <NumField label="Returns Processed per Month" value={cost.returns_per_month} onChange={(x) => u("cost_drivers.returns_per_month", x)} />
            <NumField label="3PL Reconciliation Discrepancies / Month" value={cost.reconciliation_discrepancies_per_month} onChange={(x) => u("cost_drivers.reconciliation_discrepancies_per_month", x)} />
            <NumField label="Physical Inventory Duration per Year (days)" value={cost.physical_inventory_duration_days_per_year} onChange={(x) => u("cost_drivers.physical_inventory_duration_days_per_year", x)} step="0.1" />
          </div>
        </Card>
      )}
      {tab === "investment" && (
        <>
          <Card title="Implementation Cost by Solution" subtitle="One-time implementation cost per SAP solution.">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <NumField label="StRM" value={inv.implementation_strm} onChange={(x) => u("investment.implementation_strm", x)} />
              <NumField label="EWM Basic" value={inv.implementation_ewm_basic} onChange={(x) => u("investment.implementation_ewm_basic", x)} />
              <NumField label="EWM Advanced" value={inv.implementation_ewm_advanced} onChange={(x) => u("investment.implementation_ewm_advanced", x)} />
              <NumField label="LGM" value={inv.implementation_lgm} onChange={(x) => u("investment.implementation_lgm", x)} />
            </div>
          </Card>
          <Card title="Additional Investment" subtitle="Hardware, integration and recurring cost assumptions.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <NumField label="Hardware / Devices Cost" value={inv.hardware_devices_cost} onChange={(x) => u("investment.hardware_devices_cost", x)} />
              <NumField label="Integration / Custom Dev Cost" value={inv.integration_custom_dev_cost} onChange={(x) => u("investment.integration_custom_dev_cost", x)} />
              <NumField label="Internal Business Effort Cost" value={inv.internal_business_effort_cost} onChange={(x) => u("investment.internal_business_effort_cost", x)} />
              <NumField label="Annual Support / AMS Cost" value={inv.annual_ams_cost} onChange={(x) => u("investment.annual_ams_cost", x)} hint="Recurring / year" />
              <NumField label="License / Subscription Delta" value={inv.license_subscription_delta} onChange={(x) => u("investment.license_subscription_delta", x)} hint="Annual delta" />
            </div>
          </Card>
        </>
      )}
      {tab === "risk" && (
        <Card title="Realization & Risk (%)" subtitle="Conservatism, confidence and scenario factors applied to raw ROI projections.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumField label="Year 1 Realization %" value={real.year1_realization_pct} onChange={(x) => u("realization.year1_realization_pct", x)} />
            <NumField label="Confidence Multiplier %" value={real.confidence_multiplier_pct} onChange={(x) => u("realization.confidence_multiplier_pct", x)} />
            <div />
            <NumField label="Conservative Scenario %" value={real.conservative_scenario_pct} onChange={(x) => u("realization.conservative_scenario_pct", x)} />
            <NumField label="Expected Scenario %" value={real.expected_scenario_pct} onChange={(x) => u("realization.expected_scenario_pct", x)} />
            <NumField label="Optimistic Scenario %" value={real.optimistic_scenario_pct} onChange={(x) => u("realization.optimistic_scenario_pct", x)} />
          </div>
        </Card>
      )}
    </div>
  );
}
